import { type POBillToContactModeEnum } from '../enums/POBillToContactModeEnum'
import { type POBillToPrimaryCategoryEnum } from '../enums/POBillToPrimaryCategoryEnum'
import { type SystemPreferenceKeys } from '../enums/SystemPreferenceKeys'

export type POBillToPrimaryCategorySystemPreference = {
  key: typeof SystemPreferenceKeys.PO_BILL_TO_LOCATION
  // GET 404 → treated as the default
  value: POBillToPrimaryCategoryEnum
}

export type POBillToContactSystemPreference = {
  key: typeof SystemPreferenceKeys.PO_BILL_TO_CONTACT
  value: {
    mode: POBillToContactModeEnum
    // Set only when mode === specific user; null otherwise
    userId: Nullable<number>
  }
}

export type AutoPOCustomSequenceSystemPreference = {
  key: typeof SystemPreferenceKeys.AUTO_PO_CUSTOM_SEQUENCE
  // External PO numbering on/off; GET 404 → treated as off
  value: boolean
}

export type BudgetCorrectionSystemPreference = {
  key: typeof SystemPreferenceKeys.BUDGET_CORRECTION
  value: boolean
}

export type CustomLabelsSystemPreference = {
  key: typeof SystemPreferenceKeys.CUSTOM_LABELS
  value: {
    location: {
      singular: string
      plural: string
    }
    department: {
      singular: string
      plural: string
    }
  }
}

export type DefaultDisclaimerSystemPreference = {
  key: typeof SystemPreferenceKeys.DEFAULT_DISCLAIMER
  // PODisclaimerLabel id; 0 = no default; GET 404 → treated as none
  value: number
}

export type ExpenseReimbursementSystemPreference = {
  key: typeof SystemPreferenceKeys.EXPENSE_REIMBURSEMENT
  value: string
}

export type LocaleSystemPreference = {
  key: typeof SystemPreferenceKeys.LOCALE
  value: string
}

export type MultiFactorSystemPreference = {
  key: typeof SystemPreferenceKeys.MULTI_FACTOR_AUTHENTICATION
  value: boolean
}

// Element of the po-pdf-custom-fields-hidden preference value.
// A CustomField is shown on the PO PDF when absent from this map or hidden === false.
export interface POPDFHiddenCustomField {
  customField: number
  hidden: boolean
}

export type PoPdfCustomFieldsHiddenSystemPreference = {
  key: typeof SystemPreferenceKeys.PO_PDF_CUSTOM_FIELDS_HIDDEN
  value: POPDFHiddenCustomField[]
}

export type RequesterAgreementManagementSystemPreference = {
  key: typeof SystemPreferenceKeys.REQUESTER_AGREEMENT_MANAGEMENT
  value: boolean
}

export type VendorInviteBatchLimitSystemPreference = {
  key: typeof SystemPreferenceKeys.VENDOR_INVITE_BATCH_LIMIT
  value: number
}

export type SystemPreferenceItem =
  | AutoPOCustomSequenceSystemPreference
  | BudgetCorrectionSystemPreference
  | CustomLabelsSystemPreference
  | DefaultDisclaimerSystemPreference
  | ExpenseReimbursementSystemPreference
  | LocaleSystemPreference
  | MultiFactorSystemPreference
  | POBillToContactSystemPreference
  | POBillToPrimaryCategorySystemPreference
  | PoPdfCustomFieldsHiddenSystemPreference
  | RequesterAgreementManagementSystemPreference
  | VendorInviteBatchLimitSystemPreference

export interface SystemPreferenceConstructor<T extends SystemPreferenceItem> {
  key: T['key']
  value: T['value']
}

export class SystemPreference<T extends SystemPreferenceItem> {
  key: T['key']
  value: T['value']

  constructor(data: SystemPreferenceConstructor<T>) {
    this.key = data.key
    this.value = data.value
  }
}
